import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { districts } from '../../data/mockData';
import {
  beginGoogleLoginWithSupabase,
  loginAdmin,
  loginOwner,
  registerOwnerProfile,
  setOwnerSession,
  syncSessionFromSupabase,
} from '../../data/authStore';

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [loginForm, setLoginForm] = useState({
    identifier: '',
    password: '',
  });

  const [registerForm, setRegisterForm] = useState({
    accountType: 'customer' as 'customer' | 'owner',
    fullName: '',
    phone: '',
    password: '',
    district: districts[0] || '',
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      const session = await syncSessionFromSupabase();
      if (mounted && session) navigate('/profil');
    })();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  const handleGoogleAuth = async () => {
    setError('');
    setInfo('');
    const result = await beginGoogleLoginWithSupabase();
    if (!result.ok) {
      setError(`Google ile giriş başlatılamadı: ${result.error}`);
    }
  };

  const handleGuestContinue = () => {
    setError('');
    setInfo('');
    navigate('/sahalar');
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');

    const identifier = loginForm.identifier.trim();
    const password = loginForm.password;
    if (!identifier || !password) {
      setError('Lütfen giriş bilgilerini doldurun.');
      return;
    }

    if (identifier === 'ADMİN') {
      const ok = loginAdmin(identifier, password);
      if (!ok) {
        setError('Giriş bilgileri hatalı.');
        return;
      }
      navigate('/admin/panel');
      return;
    }

    const ownerResult = loginOwner(identifier, password);
    if (!ownerResult.ok) {
      setError(ownerResult.error);
      return;
    }
    navigate('/profil');
  };

  const handleOwnerRegister = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (!registerForm.fullName || !registerForm.phone || !registerForm.password) {
      setError('Lütfen tüm alanları doldurun.');
      return;
    }

    const result = registerOwnerProfile({
      fullName: registerForm.fullName,
      phone: registerForm.phone,
      password: registerForm.password,
      district: registerForm.district,
    });
    if (!result.ok) {
      setError(result.error);
      return;
    }

    setOwnerSession(result.owner.id);
    setInfo('Profiliniz onaya gönderildi. Admin onayından sonra saha ekleyebilirsiniz.');
    navigate('/panel/saha-ekle');
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Giriş Yap</h1>
          <p className="text-sm text-gray-600 mb-6">
            Normal kullanıcılar Google ile giriş yapabilir veya misafir devam edebilir.
          </p>

          <div className="grid grid-cols-2 gap-2 bg-gray-100 rounded-lg p-1 mb-6">
            <button
              type="button"
              onClick={() => setTab('login')}
              className={`rounded-md py-2 text-sm font-medium ${tab === 'login' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
            >
              Giriş Yap
            </button>
            <button
              type="button"
              onClick={() => setTab('register')}
              className={`rounded-md py-2 text-sm font-medium ${tab === 'register' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
            >
              Hesap Aç
            </button>
          </div>

          {tab === 'login' && (
            <div className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <input
                  type="text"
                  placeholder="Telefon veya kullanıcı adı"
                  value={loginForm.identifier}
                  onChange={(e) => setLoginForm((prev) => ({ ...prev, identifier: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5"
                />
                <input
                  type="password"
                  placeholder="Şifre"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5"
                />
                <button type="submit" className="w-full rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-medium py-2.5">
                  Panel Girişi
                </button>
              </form>

              <div className="relative flex items-center my-5">
                <div className="flex-grow border-t border-gray-200" />
                <span className="flex-shrink-0 mx-4 text-xs text-gray-400">veya</span>
                <div className="flex-grow border-t border-gray-200" />
              </div>

              <div>
                <button
                  type="button"
                  onClick={handleGoogleAuth}
                  className="w-full rounded-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5"
                >
                  Google ile Giriş Yap
                </button>
              </div>

              <button
                type="button"
                onClick={handleGuestContinue}
                className="w-full rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5"
              >
                Misafir Olarak Devam Et
              </button>
            </div>
          )}

          {tab === 'register' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setRegisterForm((prev) => ({ ...prev, accountType: 'customer' }))}
                  className={`rounded-md py-2 text-sm font-medium ${registerForm.accountType === 'customer' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                >
                  Normal Kullanıcı
                </button>
                <button
                  type="button"
                  onClick={() => setRegisterForm((prev) => ({ ...prev, accountType: 'owner' }))}
                  className={`rounded-md py-2 text-sm font-medium ${registerForm.accountType === 'owner' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                >
                  Saha Sahibi
                </button>
              </div>

              {registerForm.accountType === 'customer' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Normal kullanıcılar Google hesabı ile kayıt olabilir veya misafir devam edebilir.</p>
                  <div>
                    <button
                      type="button"
                      onClick={handleGoogleAuth}
                      className="w-full rounded-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5"
                    >
                      Google ile Kayıt Ol
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleGuestContinue}
                    className="w-full rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5"
                  >
                    Misafir Olarak Devam Et
                  </button>
                </div>
              )}

              {registerForm.accountType === 'owner' && (
                <form onSubmit={handleOwnerRegister} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Ad Soyad"
                    value={registerForm.fullName}
                    onChange={(e) => setRegisterForm((prev) => ({ ...prev, fullName: e.target.value }))}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5"
                  />
                  <input
                    type="text"
                    placeholder="Telefon"
                    value={registerForm.phone}
                    onChange={(e) => setRegisterForm((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5"
                  />
                  <input
                    type="password"
                    placeholder="Şifre"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm((prev) => ({ ...prev, password: e.target.value }))}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5"
                  />
                  <select
                    value={registerForm.district}
                    onChange={(e) => setRegisterForm((prev) => ({ ...prev, district: e.target.value }))}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5"
                  >
                    {districts.map((district) => (
                      <option key={district} value={district}>
                        İstanbul / {district}
                      </option>
                    ))}
                  </select>

                  <p className="text-xs text-gray-500">
                    Onay süreci için <span className="font-semibold">0551 101 09 15</span> ile iletişime geçebilirsiniz.
                  </p>

                  <button type="submit" className="w-full rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-medium py-2.5">
                    Hesap Aç ve Onaya Gönder
                  </button>
                </form>
              )}
            </div>
          )}

          {info && (
            <p className="mt-4 text-sm rounded-md border border-blue-200 bg-blue-50 text-blue-700 px-3 py-2">{info}</p>
          )}
          {error && (
            <p className="mt-4 text-sm rounded-md border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
